import { PureComponent } from "react";
import { connect } from "react-redux";
import {
  Form,
  type InjectedFormProps,
  SubmissionError,
  type Validator,
  formValueSelector,
  reduxForm,
} from "redux-form";

import { type ActionButtonProps } from "widgets";

import { simpleDropdownFieldValidation } from "../../app-utils/form.js";
import {
  type ColumnAnnotation,
  type Dataset,
  type DatasetColumn,
  type Organism,
  type TrackExpressionColoring,
} from "../../graphql.js";
import {
  loadAvailableDatasets,
  loadDatasetMinMaxValues,
  searchDataSeries,
} from "../../redux/modules/dataset-viewer.js";
import {
  type IExpressionColoringDatasetLite,
  type ISortableTrack,
  loadDatasetForTrackExpressionColoring,
  setGenomeBrowserExpressionColoring,
} from "../../redux/modules/genome-browser.js";
import { type IAppState } from "../../redux/reducer.js";
import ConditionSelectorField, { ConditionSelectorReduxFormField } from "../core/redux-form/ConditionSelectorField.js";
import {
  ColorSchemeConfigurationFields,
  type ColorSchemeFormFields,
  DEFAULT_COLORS,
  type DefaultColorSchemeFormData,
} from "./ColorSchemeConfigurationFields.js";
import { DatasetPicker } from "./DatasetPicker.js";
import { type IFeatureTrackCustomizationDialogState } from "./features-track/FeatureTrackCustomizationDialog.js";

interface IExpressionColoringFeaturesTrackFormOwnProps {
  trackType: string;

  updateDialogState: (dialogState: IFeatureTrackCustomizationDialogState) => void;
  onClose: () => void;
}

interface IExpressionColoringFeaturesTrackFormDispatchProps {
  loadAvailableDatasets: (genomeId: number) => Promise<Dataset[]>;
  loadDataset: (datasetId: number) => Promise<Dataset>;

  setGenomeBrowserExpressionColoring: (payload: TrackExpressionColoring) => void;
  triggerDatasetPreload: (datasetId: number) => Promise<any>; // trigger an artificial search in order to trigger the loading of the dataset and therefore get the min/max values
  loadDatasetMinMaxValues: (datasetId: number) => Promise<Dataset>;
  //refreshFeatures: () => void;
}

interface IExpressionColoringFeaturesTrackFormAppStateProps {
  initialValues: Partial<any>;
  genome: Organism;
  datasets: Dataset[];
  selectedDataset: number;
  isLoadingDataset: boolean;
  datasetConditions: DatasetColumn[];
  trackExpressionColoringParams: TrackExpressionColoring;
  dataset: Dataset;
}

export interface IExpressionColoringFeaturesTrackFormProps
  extends IExpressionColoringFeaturesTrackFormAppStateProps,
    IExpressionColoringFeaturesTrackFormDispatchProps,
    IExpressionColoringFeaturesTrackFormOwnProps,
    InjectedFormProps<any, any> {}

const FORM_NAME = "expressionColoringFeaturesTrackForm";

// These are the form inputs fields name. The keys for the values from the form.
enum EXPRESSION_COLORING_FORM_FIELDS_NAME {
  DATASET = "dataset",
  CONDITIONS = "conditions",
  COLOR_SCALE = "colorScale",
  MIN = "minValue",
  MAX = "maxValue",
  LOG_SCALE = "logScale",
}

interface ExpressionColoringFormData {
  [EXPRESSION_COLORING_FORM_FIELDS_NAME.DATASET]: Dataset;
  [EXPRESSION_COLORING_FORM_FIELDS_NAME.CONDITIONS]?: ColumnAnnotation[];
  [EXPRESSION_COLORING_FORM_FIELDS_NAME.COLOR_SCALE]: DefaultColorSchemeFormData["SCALE"];
  [EXPRESSION_COLORING_FORM_FIELDS_NAME.MIN]?: DefaultColorSchemeFormData["MIN"];
  [EXPRESSION_COLORING_FORM_FIELDS_NAME.MAX]?: DefaultColorSchemeFormData["MAX"];
  [EXPRESSION_COLORING_FORM_FIELDS_NAME.LOG_SCALE]: DefaultColorSchemeFormData["LOG"];
}

function mapStateToProps(
  state: IAppState,
  ownProps: IExpressionColoringFeaturesTrackFormOwnProps,
): IExpressionColoringFeaturesTrackFormAppStateProps {
  const track: ISortableTrack = state.genomeBrowser.tracks.find((tr: ISortableTrack) => tr.type === ownProps.trackType);
  const trackExpressionColoringParams: TrackExpressionColoring =
    track && track.trackCustomization && track.trackCustomization.expressionColoring;

  const selectedDataset: Dataset = formValueSelector(FORM_NAME)(state, EXPRESSION_COLORING_FORM_FIELDS_NAME.DATASET);
  const selectedDatasetState: IExpressionColoringDatasetLite =
    selectedDataset && state.genomeBrowser.expressionColoringDatasets[selectedDataset.id];
  const initialDatasetOption: Dataset =
    trackExpressionColoringParams &&
    state.datasetViewer.availableDatasets.find(
      (dataset: Dataset) => dataset.id === trackExpressionColoringParams.datasetId,
    );

  return {
    initialValues: {
      [EXPRESSION_COLORING_FORM_FIELDS_NAME.DATASET]: initialDatasetOption,
      [EXPRESSION_COLORING_FORM_FIELDS_NAME.COLOR_SCALE]: trackExpressionColoringParams?.colorScheme ?? {
        colors: DEFAULT_COLORS,
      },
      [EXPRESSION_COLORING_FORM_FIELDS_NAME.MIN]: trackExpressionColoringParams?.minValue,
      [EXPRESSION_COLORING_FORM_FIELDS_NAME.MAX]: trackExpressionColoringParams?.maxValue,
      [EXPRESSION_COLORING_FORM_FIELDS_NAME.LOG_SCALE]: trackExpressionColoringParams?.logScale,
    },
    genome: state.genomeBrowser.genome,
    datasets: state.datasetViewer.availableDatasets,
    selectedDataset: selectedDataset && selectedDataset.id,
    isLoadingDataset: selectedDatasetState && selectedDatasetState.isLoading,
    dataset: selectedDatasetState && selectedDatasetState.dataset,
    datasetConditions: selectedDatasetState && selectedDatasetState.dataset && selectedDatasetState.dataset.columns,
    trackExpressionColoringParams: trackExpressionColoringParams,
  };
}

function mapDispatchToProps(
  dispatch,
  ownProps: IExpressionColoringFeaturesTrackFormOwnProps,
): IExpressionColoringFeaturesTrackFormDispatchProps {
  return {
    loadAvailableDatasets: (genomeId: number) => dispatch(loadAvailableDatasets(genomeId)),
    loadDataset: (datasetId: number) => dispatch(loadDatasetForTrackExpressionColoring(datasetId)),
    setGenomeBrowserExpressionColoring: (payload: TrackExpressionColoring) =>
      dispatch(setGenomeBrowserExpressionColoring(ownProps.trackType, payload)),
    triggerDatasetPreload: (datasetId: number) =>
      dispatch(searchDataSeries("triggerDatasetLoadingInOrderToGetMinMaxValues", datasetId)),
    loadDatasetMinMaxValues: (datasetId: number) => dispatch(loadDatasetMinMaxValues(datasetId)),
  };
}

interface IExpressionColoringFeaturesTrackFormState {
  isLoadingAvailableDatasets: boolean;
}

class ExpressionColoringFeaturesTrackForm extends PureComponent<
  IExpressionColoringFeaturesTrackFormProps,
  IExpressionColoringFeaturesTrackFormState
> {
  state = {
    isLoadingAvailableDatasets: false,
  };

  colorSchemeFormFields: ColorSchemeFormFields = {
    SCALE: { name: EXPRESSION_COLORING_FORM_FIELDS_NAME.COLOR_SCALE },
    MIN: {
      name: EXPRESSION_COLORING_FORM_FIELDS_NAME.MIN,
      validate: ExpressionColoringFeaturesTrackForm.minMaxFieldValidation,
    },
    MAX: {
      name: EXPRESSION_COLORING_FORM_FIELDS_NAME.MAX,
      validate: ExpressionColoringFeaturesTrackForm.minMaxFieldValidation,
    },
    LOG: {
      name: EXPRESSION_COLORING_FORM_FIELDS_NAME.LOG_SCALE,
      validate: ExpressionColoringFeaturesTrackForm.datasetExpressionConfigDropdownFieldValidation,
    },
  };

  static getConditionOptions(datasetConditions?: DatasetColumn[]): ColumnAnnotation[] | null {
    return datasetConditions
      ? datasetConditions.map((o: DatasetColumn) => ({
          field: o.field,
          name: o.name,
          type: null,
          valueSet: null,
          values: null,
        }))
      : null;
  }

  setDialogFooterProps = () => {
    this.props.updateDialogState({
      nextButtonProps: {
        label: "Apply",
        isLoading: this.props.submitting,
        tooltip: "Apply the coloring to the track features",
        onPress: this.props.handleSubmit((values: ExpressionColoringFormData) =>
          this.handleSubmitData(values).then(() => this.props.onClose()),
        ),
      },
      resetButton: {
        label: "Reset",
        onPress: () => {
          this.props.initialize({});
          this.props.reset();
        },
      } as ActionButtonProps,
    });
  };

  componentDidMount() {
    this.setDialogFooterProps();

    this.setState({ isLoadingAvailableDatasets: true });
    this.props
      .loadAvailableDatasets(this.props.genome.id)
      .then(() => this.setState({ isLoadingAvailableDatasets: false }));
  }

  componentDidUpdate(prevProps: IExpressionColoringFeaturesTrackFormProps) {
    this.setDialogFooterProps();

    if (this.props.selectedDataset && prevProps.selectedDataset !== this.props.selectedDataset) {
      void this.props.loadDataset(this.props.selectedDataset).then((dataset: Dataset) => {
        let selectedConditions: ColumnAnnotation[] = []; // reset the selected experiments/conditions
        // in case of existing initial dataset => we need to infer the needed data for the selected experiments/conditions
        if (
          this.props.trackExpressionColoringParams &&
          this.props.trackExpressionColoringParams.datasetId === this.props.selectedDataset
        ) {
          const allConditionOptions = ExpressionColoringFeaturesTrackForm.getConditionOptions(dataset.columns);
          selectedConditions =
            this.props.trackExpressionColoringParams.conditions // we start with this list in order to keep the correct ORDER
              ?.map((selectedCondition) =>
                allConditionOptions?.find((a: ColumnAnnotation) => a.field === selectedCondition),
              )
              .filter((a: ColumnAnnotation) => !!a) ?? []; // in case the selected condition was not found above
        }

        this.props.change(EXPRESSION_COLORING_FORM_FIELDS_NAME.CONDITIONS, selectedConditions);
      });
    }
  }

  handleSubmitData = (values: ExpressionColoringFormData): Promise<void | boolean> => {
    const dataset = values[EXPRESSION_COLORING_FORM_FIELDS_NAME.DATASET];
    const conditions = values[EXPRESSION_COLORING_FORM_FIELDS_NAME.CONDITIONS];
    const colorScheme = values[EXPRESSION_COLORING_FORM_FIELDS_NAME.COLOR_SCALE];
    const minValue = values[EXPRESSION_COLORING_FORM_FIELDS_NAME.MIN];
    const maxValue = values[EXPRESSION_COLORING_FORM_FIELDS_NAME.MAX];
    const logScale = values[EXPRESSION_COLORING_FORM_FIELDS_NAME.LOG_SCALE];

    if (!dataset || !conditions?.length) {
      // meant to clear the expression coloring
      this.props.setGenomeBrowserExpressionColoring(null);
      return Promise.resolve(true);
    }

    // we need to back-translate the selected conditions into strings
    const finalConditions = conditions.map((c: ColumnAnnotation) => c.field);

    // before getting the min/max values of the selected condition, we need to make sure the dataset has been
    // parsed already! otherwise the min/max values won't be filled, as discussed with Ed
    return this.props
      .triggerDatasetPreload(dataset.id)
      .then(
        () => {
          return this.props.loadDatasetMinMaxValues(dataset.id).then((dataset: Dataset) => {
            // As we are using falsy/truthy test later, 0 will be considered as undefined
            let globalMin = 0;
            let globalMax = 0;

            // calculate the global min and max over all selected conditions
            finalConditions.forEach((condition: string) => {
              const column = dataset?.columns?.find((c) => c?.field === condition);

              if (!column) {
                throw new Error(`Could not load condition ${condition}`);
              }

              globalMin = globalMin ? Math.min(globalMin, column.minValue ?? globalMin) : (column.minValue ?? 0);
              globalMax = globalMax ? Math.max(globalMax, column.maxValue ?? globalMax) : (column.maxValue ?? 0);
            });

            const payload: TrackExpressionColoring = {
              datasetId: dataset.id,
              conditions: finalConditions,
              colorScheme: colorScheme,
              minValue: (minValue && Number.parseFloat(minValue)) || globalMin,
              maxValue: (maxValue && Number.parseFloat(maxValue)) || globalMax,
              logScale,
            };

            if (payload.logScale && payload.minValue! <= 0) {
              throw new Error("Selected experiment data contains values <= 0. This is incompatible with log scale.");
            }

            this.props.setGenomeBrowserExpressionColoring(payload);
            return true;
          });
        },
        // the following will throw an error that redux-form understands as a submission error, with a general error that will be
        // available from this.props.error
        (rejected) => {
          throw new SubmissionError({ _error: rejected.message });
        },
      )
      .catch((error) => {
        throw new SubmissionError({ _error: error.message });
      });
  };

  onDatasetSelect = (selectedDataset: Dataset) => {
    this.props.change(EXPRESSION_COLORING_FORM_FIELDS_NAME.DATASET, selectedDataset);
    this.props.change(EXPRESSION_COLORING_FORM_FIELDS_NAME.CONDITIONS, []);
  };

  render() {
    // we only provide a list of options if a dataset is selected
    const conditionOptions: ColumnAnnotation[] = this.props.selectedDataset
      ? ExpressionColoringFeaturesTrackForm.getConditionOptions(this.props.datasetConditions)
      : [];

    return (
      <Form
        className={"expression-coloring-form"}
        onSubmit={() => {
          /* empty */
        }}
      >
        <ColorSchemeConfigurationFields fields={this.colorSchemeFormFields} />
        <DatasetPicker
          label={"Dataset"}
          isLoading={this.state.isLoadingAvailableDatasets}
          datasets={this.props.datasets}
          selectedDatasetId={this.props.selectedDataset}
          handleSelectedDatasetChanged={this.onDatasetSelect}
        />
        <ConditionSelectorReduxFormField
          name={EXPRESSION_COLORING_FORM_FIELDS_NAME.CONDITIONS}
          horizontalLayout={false}
          component={ConditionSelectorField}
          label={"Experiments"}
          options={conditionOptions}
          tooltip={
            this.props.selectedDataset
              ? "Choose the dataset experiments corresponding to the expression values to be mapped to the track features"
              : "Once you selected a dataset, this field will allow you to choose experiments used for the expression coloring"
          }
          addOptionMessage={"Add experiment"}
          limit={10}
          limitReachedMessage={"Limit of 10 experiments reached"}
          hideConditionSortDialogButton={true}
          validate={ExpressionColoringFeaturesTrackForm.conditionSelectorFieldValidation}
        />
      </Form>
    );
  }

  static conditionSelectorFieldValidation: Validator = (
    value: ColumnAnnotation[],
    allValues: ExpressionColoringFormData,
    props: IExpressionColoringFeaturesTrackFormProps,
  ) => {
    const datasetDefined = !!allValues[EXPRESSION_COLORING_FORM_FIELDS_NAME.DATASET];
    return datasetDefined && props.dataset && !(value && value.length > 0)
      ? "At least one experiment must be chosen."
      : undefined;
  };

  static datasetExpressionConfigDropdownFieldValidation = (
    value: boolean,
    allValues: ExpressionColoringFormData,
  ): Validator => {
    const datasetDefined = !!allValues[EXPRESSION_COLORING_FORM_FIELDS_NAME.DATASET];
    return datasetDefined ? simpleDropdownFieldValidation(value) : undefined;
  };

  // TODO: The expression rendering should support zero and negative values for the log by normalizing the value to log10?
  static minMaxFieldValidation = (value: string, allValues: ExpressionColoringFormData) => {
    const min: number = Number.parseFloat(allValues[EXPRESSION_COLORING_FORM_FIELDS_NAME.MIN]);
    const max: number = Number.parseFloat(allValues[EXPRESSION_COLORING_FORM_FIELDS_NAME.MAX]);
    const isLog: boolean = allValues[EXPRESSION_COLORING_FORM_FIELDS_NAME.LOG_SCALE];
    const numValue: number = Number.parseFloat(value);

    return min >= max
      ? "The min value must be smaller than the max value"
      : isLog && numValue <= 0
        ? "Log scale is not possible with limits <= 0"
        : undefined;
  };
}

const ExpressionColoringFeaturesTrackFormComponent = connect<
  IExpressionColoringFeaturesTrackFormAppStateProps,
  IExpressionColoringFeaturesTrackFormDispatchProps,
  IExpressionColoringFeaturesTrackFormOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm<ExpressionColoringFormData>({
    form: FORM_NAME,
    enableReinitialize: true,
  })(ExpressionColoringFeaturesTrackForm) as any,
);

export { ExpressionColoringFeaturesTrackFormComponent as ExpressionColoringFeaturesTrackForm };
